import type {ReactNode} from 'react';
import {AlertTriangle, ChevronRight, OctagonAlert} from 'lucide-react';
import {cn} from '@/lib/utils/cn';
import {Badge} from '@/components/ui/Badge';
import type {IssueCounts} from './lib/sections';

interface CollapsibleSectionProps {
  title: ReactNode;
  /** Short plain-text preview shown next to the title while collapsed. */
  preview?: ReactNode;
  issues: IssueCounts;
  /** Hover text for the header, e.g. what kind of data the panel holds. */
  tooltip?: string;
  /** 'section' for a top-level data-type panel, 'row' for a party/line
   * nested inside one. */
  level?: 'section' | 'row';
  children: ReactNode;
}

export function IssueBadges({issues}: {issues: IssueCounts}) {
  return (
    <>
      {issues.errors > 0 && (
        <Badge variant="destructive">
          <OctagonAlert className="h-3 w-3" />
          {issues.errors} error{issues.errors === 1 ? '' : 's'}
        </Badge>
      )}
      {issues.warnings > 0 && (
        <Badge variant="warning">
          <AlertTriangle className="h-3 w-3" />
          {issues.warnings} warning{issues.warnings === 1 ? '' : 's'}
        </Badge>
      )}
    </>
  );
}

/**
 * A collapsed-by-default panel whose header already shows how many errors
 * and warnings are inside it (and is tinted to match), so problems are
 * visible before anything is expanded. Built on native <details> so its open
 * state survives re-renders from edits, and so revealFinding() can open it.
 */
export function CollapsibleSection({
  title,
  preview,
  issues,
  tooltip,
  level = 'section',
  children,
}: CollapsibleSectionProps) {
  const severity =
    issues.errors > 0 ? 'error' : issues.warnings > 0 ? 'warning' : undefined;

  return (
    <details
      className={cn(
        'group border',
        level === 'section' ? 'rounded-xl bg-card shadow-sm' : 'rounded-lg',
        severity === 'error' && 'border-destructive/60 bg-destructive/5',
        severity === 'warning' && 'border-warning/60 bg-warning/5',
        !severity && 'border-border'
      )}
    >
      <summary
        title={tooltip}
        className={cn(
          'flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden',
          level === 'section' ? 'px-4 py-2.5' : 'px-3 py-1.5'
        )}
      >
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <span
          className={cn(
            'shrink-0',
            level === 'section'
              ? 'text-sm font-semibold'
              : 'text-sm font-medium'
          )}
        >
          {title}
        </span>
        {preview && (
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {preview}
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          <IssueBadges issues={issues} />
        </span>
      </summary>
      <div
        className={cn(
          'space-y-1.5',
          level === 'section' ? 'px-4 pb-3' : 'px-3 pb-2'
        )}
      >
        {children}
      </div>
    </details>
  );
}

/** Opens every collapsed panel containing the field a finding is about,
 * then scrolls to and focuses it. Falls back to the field's segment when the
 * element itself isn't rendered (e.g. a required element missing off the end
 * of the segment). Returns false when neither is on the page. */
export function revealFinding(findingPath: string): boolean {
  const segmentPath = findingPath.replace(/\.elements\[\d+\]$/, '');
  const field =
    document.getElementById(`field-${findingPath}`) ??
    document.getElementById(`segment-${segmentPath}`);
  if (!field) return false;

  for (
    let d = field.closest('details');
    d;
    d = d.parentElement?.closest('details') ?? null
  ) {
    d.open = true;
  }
  field.scrollIntoView({block: 'center', behavior: 'smooth'});
  field.focus({preventScroll: true});
  return true;
}
